import { computed, ComputedRef } from 'vue';
import { InitialPublicOfferItem } from '@libs/api-reference';

import { FLOW_STYLES_TYPE, IPO_INITIAL_STATES } from '../constants';
import {
  useAfterEndOrder,
  useBeforeEndPreOrder,
  useBetweenEndPreOrderAndStartOrder,
  useBetweenStartOrderAndEndOrder,
} from '../../src';
import { offerHasPrice } from '../utils';

type UseIPOMachineType = {
  offer: ComputedRef<InitialPublicOfferItem>;
  isOpenOrder: ComputedRef<boolean>;
  isDoneOrder: ComputedRef<boolean>;
  buyingPower: ComputedRef<number>;
};

export default function useIPOMachine({
  offer,
  buyingPower,
  isOpenOrder,
  isDoneOrder,
}: UseIPOMachineType) {
  const hasPurchase = computed(() => {
    if (!offer.value.hasPreOrder && offer.value.isAutomaticSubmit) {
      return (
        buyingPower.value >=
        offer.value.maximumOrderPrice * offer.value.maximumOrderQuantity
      );
    }

    return (
      buyingPower.value >=
      Number(offer.value.price) * Number(offer.value.quantity)
    );
  });

  const hasPrice = offerHasPrice(offer.value);

  return computed<{ time: string; stylesConfig: FLOW_STYLES_TYPE }>(() =>
    ({
      [IPO_INITIAL_STATES.beforePreOrderEnd]: () =>
        useBeforeEndPreOrder({ offer: offer.value, hasPurchase, hasPrice }),
      [IPO_INITIAL_STATES.betweenEndPreOrderAndStartOrder]: () =>
        useBetweenEndPreOrderAndStartOrder({
          offer: offer.value,
          hasPurchase,
          isDoneOrder: isDoneOrder.value,
          hasPrice,
        }),
      [IPO_INITIAL_STATES.betweenStartOrderAndEndOrder]: () =>
        useBetweenStartOrderAndEndOrder({
          offer: offer.value,
          isOpenOrder: isOpenOrder.value,
          isDoneOrder: isDoneOrder.value,
        }),
      [IPO_INITIAL_STATES.afterEndOrder]: () =>
        useAfterEndOrder({
          offer: offer.value,
          isOpenOrder: isOpenOrder.value,
          isDoneOrder: isDoneOrder.value,
        }),
    }[offer.value.state]())
  );
}
